import { getCookie } from './cokie'

export enum Country {
    IN = 'IN',
    SG = 'SG',
    AE = 'AE',
    MY = 'MY',
    PHL = 'PHL',
}

export const CountryTenants: Record<Country, string[]> = {
    [Country.IN]: ['1001', '1002'],
    [Country.SG]: ['1200', '1405', '1150'],
    [Country.AE]: [],
    [Country.MY]: ['1050'],
    [Country.PHL]: [],
}

export interface TenantConfig {
    country: Country
    countryName: string
    currencySymbol: string
    currencyCode: string
    phoneCountryCode: string
    phoneMaxLength: number
    defaultCity: string
    kycDocumentLabel: string
    bankCodeLabel: string
    cancelChequeLabel: string
    poOptions: string[]
    onlyPineAcquiring: boolean
    onlinePoDisabled: boolean
}

const countryConfig: Record<Country, TenantConfig> = {
    [Country.IN]: {
        country: Country.IN,
        countryName: 'India',
        currencySymbol: '₹',
        currencyCode: 'INR',
        phoneCountryCode: '+91',
        phoneMaxLength: 10,
        defaultCity: '',
        kycDocumentLabel: 'PAN',
        bankCodeLabel: 'IFSC Code',
        cancelChequeLabel: 'Upload Cancel Cheque',
        poOptions: ['Online PO', 'Offline PO'],
        onlyPineAcquiring: false,
        onlinePoDisabled: false,
    },
    [Country.SG]: {
        country: Country.SG,
        countryName: 'Singapore',
        currencySymbol: 'SGD',
        currencyCode: 'SGD',
        phoneCountryCode: '+65',
        phoneMaxLength: 8,
        defaultCity: 'Singapore',
        kycDocumentLabel: 'UEN',
        bankCodeLabel: 'Swift Code',
        cancelChequeLabel: 'Upload Bank Authorization Letter',
        poOptions: ['Offline PO'],
        onlyPineAcquiring: true,
        onlinePoDisabled: true,
    },
    [Country.AE]: {
        country: Country.AE,
        countryName: 'UAE',
        currencySymbol: 'AED',
        currencyCode: 'AED',
        phoneCountryCode: '+971',
        phoneMaxLength: 9,
        defaultCity: '',
        kycDocumentLabel: 'TIN',
        bankCodeLabel: 'Swift Code / BIC Code',
        cancelChequeLabel: 'Upload Bank Authorization Letter',
        poOptions: ['Offline PO'],
        onlyPineAcquiring: true,
        onlinePoDisabled: true,
    },
    [Country.MY]: {
        country: Country.MY,
        countryName: 'Malaysia',
        currencySymbol: 'RM',
        currencyCode: 'MYR',
        phoneCountryCode: '+60',
        phoneMaxLength: 10,
        defaultCity: '',
        kycDocumentLabel: 'SSM',
        bankCodeLabel: 'Swift Code',
        cancelChequeLabel: 'Upload Bank Authorization Letter',
        poOptions: ['Offline PO'],
        onlyPineAcquiring: true,
        onlinePoDisabled: true,
    },
    [Country.PHL]: {
        country: Country.PHL,
        countryName: 'Philippines',
        currencySymbol: '₱',
        currencyCode: 'PHP',
        phoneCountryCode: '+63',
        phoneMaxLength: 10,
        defaultCity: '',
        kycDocumentLabel: 'TIN',
        bankCodeLabel: 'Swift Code',
        cancelChequeLabel: 'Upload Bank Authorization Letter',
        poOptions: ['Offline PO'],
        onlyPineAcquiring: true,
        onlinePoDisabled: true,
    },
}

const tenantToCountry: Record<string, Country> = {}
Object.entries(CountryTenants).forEach(([country, tenantIds]) => {
    tenantIds.forEach((id) => {
        tenantToCountry[id] = country as Country
    })
})

// Tenants that use one country's flow but display as a different country.
// PHL and UAE share the SG route family while retaining their country settings.
const tenantDisplayOverride: Record<string, Country> = {
    '1405': Country.PHL,
    '1150': Country.AE,
}

// Login-specific tenant → country mapping.
// Used only on the login page to determine which region/flag to display.
// 1405 is treated as PHL for login (region selector, flag, label),
// even though internally it routes through the SG flow.
export const LOGIN_COUNTRY_TENANTS: Record<Country, string[]> = {
    [Country.IN]: ['1001', '1002'],
    [Country.SG]: ['1200'],
    [Country.AE]: ['1150'],
    [Country.MY]: ['1050'],
    [Country.PHL]: ['1405'],
}

const loginTenantToCountry: Record<string, Country> = {}
Object.entries(LOGIN_COUNTRY_TENANTS).forEach(([country, tenantIds]) => {
    tenantIds.forEach((id) => {
        loginTenantToCountry[id] = country as Country
    })
})

export const getLoginCountry = (tenantId: string): Country | null => {
    return loginTenantToCountry[tenantId] ?? null
}

export const isLoginPhilippines = (tenantId: string): boolean => {
    return loginTenantToCountry[tenantId] === Country.PHL
}

const defaultConfig: TenantConfig = countryConfig[Country.SG]

export const getCurrentTenantId = (): string => {
    return getCookie('tenantId') || CountryTenants[Country.SG][0]
}

export const getTenantConfig = (tenantId?: string): TenantConfig => {
    const id = tenantId || getCurrentTenantId()
    const displayCountry = tenantDisplayOverride[id]
    if (displayCountry) return countryConfig[displayCountry]
    const country = tenantToCountry[id]
    return country ? countryConfig[country] : defaultConfig
}

export const getCountry = (tenantId?: string): Country => {
    const id = tenantId || getCurrentTenantId()
    return tenantToCountry[id] ?? defaultConfig.country
}

export const isSingapore = (tenantId?: string): boolean => {
    return getCountry(tenantId) === Country.SG
}

export const isPhilippines = (tenantId?: string): boolean => {
    const id = tenantId || getCurrentTenantId()
    return tenantDisplayOverride[id] === Country.PHL
}

export const isIndia = (tenantId?: string): boolean => {
    return getCountry(tenantId) === Country.IN
}

// Use this check for UAE-only behavior, even when it shares another tenant's flow.
export const isUAE = (tenantId?: string): boolean => {
    return getTenantConfig(tenantId).country === Country.AE
}

// UAE reuses PHL screens and validation, not its country identity or branding.
export const isPhilippinesFlow = (tenantId?: string): boolean => {
    return isPhilippines(tenantId) || isUAE(tenantId)
}

export const isMalaysia = (tenantId?: string): boolean => {
    return getCountry(tenantId) === Country.MY
}

// Returns the display country name for the current tenant.
// PHL is checked first because isSingapore() also returns true for tenant 1405 (PHL routes through SG).
export const getTenantWiseCountry = (tenantId?: string): string => {
    if (isPhilippines(tenantId)) return 'Philippines'
    if (isUAE(tenantId)) return 'UAE'
    if (isSingapore(tenantId)) return 'Singapore'
    if (isMalaysia(tenantId)) return 'Malaysia'
    return ''
}

export const getCountryName = (tenantId?: string): string => {
    return getTenantConfig(tenantId).countryName
}

export const getCurrencySymbol = (tenantId?: string): string => {
    return getTenantConfig(tenantId).currencySymbol
}

export const getPhoneCountryCode = (tenantId?: string): string => {
    return getTenantConfig(tenantId).phoneCountryCode
}

export const getPhoneMaxLength = (tenantId?: string): number => {
    return getTenantConfig(tenantId).phoneMaxLength
}

export const getDefaultCity = (tenantId?: string): string => {
    return getTenantConfig(tenantId).defaultCity
}

export const getTaxPercentage = (tenantId?: string): number => {
    if (isMalaysia(tenantId)) return 18
    if (isUAE(tenantId)) return 0
    if (isPhilippines(tenantId)) return 9
    if (isSingapore(tenantId)) return 9
    return 0
}

// Tax type label per region. Malaysia uses SST instead of a percentage number.
export const getTaxLabel = (tenantId?: string): string => {
    if (isMalaysia(tenantId)) return 'SST'
    return ''
}

export const getKycDocumentLabel = (tenantId?: string): string => {
    return getTenantConfig(tenantId).kycDocumentLabel
}

export const getBankCodeLabel = (tenantId?: string): string => {
    return getTenantConfig(tenantId).bankCodeLabel
}

export const getCancelChequeLabel = (tenantId?: string): string => {
    return getTenantConfig(tenantId).cancelChequeLabel
}

export const getPoOptions = (tenantId?: string): string[] => {
    return getTenantConfig(tenantId).poOptions
}

export const isOnlyPineAcquiring = (tenantId?: string): boolean => {
    return getTenantConfig(tenantId).onlyPineAcquiring
}

export const isOnlinePoDisabled = (tenantId?: string): boolean => {
    return getTenantConfig(tenantId).onlinePoDisabled
}

export const COUNTRY_LIST = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
    'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
    'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize',
    'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil',
    'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi',
    'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada', 'Central African Republic', 'Chad',
    'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba',
    'Cyprus', 'Czech Republic',
    'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
    'East Timor', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea',
    'Estonia', 'Eswatini', 'Ethiopia',
    'Fiji', 'Finland', 'France',
    'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala',
    'Guinea', 'Guinea-Bissau', 'Guyana',
    'Haiti', 'Honduras', 'Hungary',
    'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
    'Jamaica', 'Japan', 'Jordan',
    'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan',
    'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein',
    'Lithuania', 'Luxembourg',
    'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
    'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia',
    'Montenegro', 'Morocco', 'Mozambique', 'Myanmar',
    'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger',
    'Nigeria', 'North Korea', 'North Macedonia', 'Norway',
    'Oman',
    'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
    'Philippines', 'Poland', 'Portugal',
    'Qatar',
    'Romania', 'Russia', 'Rwanda',
    'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa',
    'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia',
    'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands',
    'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka',
    'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
    'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Togo', 'Tonga',
    'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
    'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
    'Uruguay', 'Uzbekistan',
    'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
    'Yemen',
    'Zambia', 'Zimbabwe',
]

export const getCountryDropdownList = () => {
    return COUNTRY_LIST.map((country) => ({ key: country, value: country }))
}
